import type { AppNotification } from '../../services/notification';
import NotificationContentRender, { type ElapsedTimeLabels, type NotificationTextsShape } from "./NotificationContentRender";

function notificationFrameIcons(category: string) {
    const fillColorMap: Record<string, string> = {
        Cases: '#CC0000',
        Agreements: '#0079D0',
        Assets: '#ECC600',
    };
    const fillColor = fillColorMap[category] || '#CC0000';
    return (
        <svg
            fill="none"
            height="12"
            viewBox="0 0 18 12"
            width="18"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M2 0C0.896875 0 0 0.896875 0 2V4C0 4.275 0.23125 4.49063 0.490625 4.58125C1.07812 4.78438 1.5 5.34375 1.5 6C1.5 6.65625 1.07812 7.21562 0.490625 7.41875C0.23125 7.50937 0 7.725 0 8V10C0 11.1031 0.896875 12 2 12H16C17.1031 12 18 11.1031 18 10V8C18 7.725 17.7687 7.50937 17.5094 7.41875C16.9219 7.21562 16.5 6.65625 16.5 6C16.5 5.34375 16.9219 4.78438 17.5094 4.58125C17.7687 4.49063 18 4.275 18 4V2C18 0.896875 17.1031 0 16 0H2ZM4 3.5V8.5C4 8.775 4.225 9 4.5 9H13.5C13.775 9 14 8.775 14 8.5V3.5C14 3.225 13.775 3 13.5 3H4.5C4.225 3 4 3.225 4 3.5ZM3 3C3 2.44688 3.44688 2 4 2H14C14.5531 2 15 2.44688 15 3V9C15 9.55313 14.5531 10 14 10H4C3.44688 10 3 9.55313 3 9V3Z"
                fill={fillColor}
            />
        </svg>
    );
}

function getShadowColor(category: string): string {
    const shadowColorMap: Record<string, string> = {
        Cases: '#CC0000',
        Agreements: '#0079D0',
        Assets: '#ECC600',
    };
    return shadowColorMap[category] || '#ffeb3b';
}


interface AssetsListingPageLink {
    productId: string;
    productFamily: string;
    link: string;
}

interface NotificationItemProps {
    notification: AppNotification;
    notificationTexts?: NotificationTextsShape;
    links?:{
        "caseDetailsPageUrl": string;
        "agreementDetailsPageUrl": string;
        "assetsListingPageLinks": AssetsListingPageLink[];
        "contactUsPageUrl": string;
    }
    elapsedTimeLabels?: ElapsedTimeLabels;
    unreadLabel?: string;
    onMarkAsRead?: (notificationId: string) => void;
}

function getCaseUrl(data: AppNotification['data'], links: NotificationItemProps['links']): string {
    const caseId = data?.caseId;
    return caseId ? `${links.caseDetailsPageUrl}/${caseId}` : links.caseDetailsPageUrl;
}

function getAgreementUrl(
    data: AppNotification['data'],
    links: NotificationItemProps['links'],
): string {
    const agreementId = data?.id;
    return agreementId
        ? `${links.agreementDetailsPageUrl}/${agreementId}`
        : links.agreementDetailsPageUrl;
}

function getAssetUrl(data: AppNotification['data'], links: NotificationItemProps['links']): string {
    const assetLinks = links.assetsListingPageLinks;
    if (!Array.isArray(assetLinks) || assetLinks.length === 0) return '#';

    const productCategoryId = data?.productCategoryId;
    const matched =
        productCategoryId != null
            ? assetLinks.find((item) => item.productId === String(productCategoryId))
            : undefined;

    const target = matched ?? assetLinks[0];
    const baseUrl = target.link;
    if (!baseUrl) return '#';

    const params = new URLSearchParams();
    if (target.productFamily) params.set('product-category-name', target.productFamily);
    if (productCategoryId != null) params.set('product-category-id', String(productCategoryId));
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
}

function getCategoryUrl(
    category: string,
    data: AppNotification['data'],
    links?: NotificationItemProps['links'],
): string {
    if (!links) return '#';

    switch (category) {
        case 'Cases':
            return getCaseUrl(data, links);
        case 'Agreements':
            return getAgreementUrl(data, links);
        case 'Assets':
            return getAssetUrl(data, links);
        default:
            return '#';
    }
}

function getViewLinkLabel(category: string, notificationTexts?: NotificationTextsShape): string {
    if (!notificationTexts) return 'View ' + category;
    const { cases, agreements, assets } = notificationTexts as {
        cases?: Record<string, string>;
        agreements?: Record<string, string>;
        assets?: Record<string, string>;
    };
    if (category === 'Cases') return cases?.['view-case'] ?? 'View Case';
    if (category === 'Agreements') return agreements?.['view-agreement'] ?? 'View Agreement';
    if (category === 'Assets') return assets?.['view-assets'] ?? 'View Asset';
    return 'View ' + category;
}

const NotificationItem = ({ notification, notificationTexts, links, elapsedTimeLabels, unreadLabel, onMarkAsRead }: NotificationItemProps) => {
    const { id, isRead,type,category,data ,createdAt , actions} = notification
    const shadowColor = getShadowColor(category)
    const categoryUrl = getCategoryUrl(category, data, links)
    const viewLinkLabel = getViewLinkLabel(category, notificationTexts)

    const handleMarkAsRead = () => {
        if (onMarkAsRead) {
            onMarkAsRead(id)
        }
    }

    return (
        <div className={`cop-notification__item ${!isRead ? 'cop-notification__item--unread' : ''}`}>
            <div
                className="cop-notification__icon"
                style={{ boxShadow: `0 0 0 2px ${shadowColor}` }}
            >
                {notificationFrameIcons(category)}
            </div>
            <div className="cop-notification__body">
                <NotificationContentRender data={data || {}} createdAt={createdAt} notificationType={type} notificationTexts={notificationTexts} actions={actions} contactUsLink={links.contactUsPageUrl} elapsedTimeLabels={elapsedTimeLabels} />
                <div className="cop-notification__footer">
                    <div className="cop-notification__footer-left">
                        <a
                            href={categoryUrl}
                            className="cop-notification__link"
                            onClick={() => { if (!isRead) handleMarkAsRead(); }}
                        >
                            {viewLinkLabel}
                        </a>
                    </div>
                    <div className="cop-notification__footer-right">
                        {!isRead && <span className="cop-notification__unread-indicator"></span>}
                        {!isRead && (
                            <button
                                type="button"
                                className="cop-notification__unread-link"
                                onClick={handleMarkAsRead}
                            >
                                {unreadLabel || 'Unread'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default NotificationItem;